// Segment page — agencies. Replaces /pour/agences-medias stub with a
// page that argues "10 client sites without hiring" via differentiated
// value props, a side-by-side time-saved calculation block, white-label
// highlight, and pricing teaser (Growth or Network).

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Sparkles, LayoutDashboard, Palette, Users2, Receipt } from "lucide-react";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { MarketingSegmentLayout } from "@/components/marketing/MarketingSegmentLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { withUtm } from "@/lib/marketing-utm";
import { PLAN_DISPLAY_NAMES, PLAN_MONTHLY_CENTS } from "@/lib/marketing-plan-matrix";
import { useLocale } from "@/lib/locale";
import { formatMoney, planMonthlyCents } from "@/lib/currency";

const UTM_CAMPAIGN = "agences-medias";

const VALUE_PROPS = [
  { key: "multiSiteDashboard", Icon: LayoutDashboard },
  { key: "whiteLabel", Icon: Palette },
  { key: "teamSeats", Icon: Users2 },
  { key: "centralizedBilling", Icon: Receipt },
] as const;

export default function AgencyPage() {
  const { t, i18n } = useTranslation();
  const locale = useLocale();

  const pains = [0, 1, 2].map((i) => ({
    title: t(`marketing.pages.agency.pains.${i}.title`),
    body: t(`marketing.pages.agency.pains.${i}.body`),
  }));
  const solutions = [0, 1, 2].map((i) => ({
    title: t(`marketing.pages.agency.solutions.${i}.title`),
    body: t(`marketing.pages.agency.solutions.${i}.body`),
  }));
  const networkPrice = formatMoney(planMonthlyCents("network")!, locale);

  return (
    <MarketingSegmentLayout
      seoTitle={t("marketing.pages.agency.seo.title")}
      seoDescription={t("marketing.pages.agency.seo.description")}
      path="/pour/agences-medias"
      breadcrumbName={t("marketing.header.personas.agenciesMedia.title")}
      eyebrow={t("marketing.pages.agency.hero.eyebrow")}
      heroTitle={t("marketing.pages.agency.hero.title")}
      heroLead={t("marketing.pages.agency.hero.lead")}
      pains={pains}
      painsHeading={t("marketing.pages.agency.painsHeading")}
      solutions={solutions}
      solutionsHeading={t("marketing.pages.agency.solutionsHeading")}
      utmCampaign={UTM_CAMPAIGN}
      ctaHeadline={t("marketing.pages.agency.cta.headline")}
      ctaSubhead={t("marketing.pages.agency.cta.subhead")}
      primaryCtaLabel={t("marketing.pages.agency.cta.primary")}
      primaryCtaTo="/dpadmin?intent=signup&plan=network"
      secondaryCtaLabel={t("marketing.cta.bookDemo")}
      secondaryCtaTo="/contact?subject=agence"
    >
      <Rule />

      {/* Differentiated value props (4-up) */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.agency.values.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.agency.values.title")}
          </DisplayHeading>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {VALUE_PROPS.map(({ key, Icon }) => (
            <article key={key} className="border border-rule p-6">
              <Icon className="h-5 w-5 text-accent-press" aria-hidden />
              <h3 className="mt-4 text-base font-medium tracking-tight">
                {t(`marketing.pages.agency.values.items.${key}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t(`marketing.pages.agency.values.items.${key}.body`)}
              </p>
            </article>
          ))}
        </div>
      </Section>

      <Rule />

      {/* "Le calcul d'une agence" block */}
      <Section padding="md" tone="paper">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.agency.math.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.agency.math.title")}
          </DisplayHeading>
          <p className="mt-4 text-base leading-relaxed text-ink/70">
            {t("marketing.pages.agency.math.lead")}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          <article className="border border-rule bg-background p-6">
            <h3 className="dateline">{t("marketing.pages.agency.math.before.heading")}</h3>
            <ul className="mt-4 space-y-3">
              {(t("marketing.pages.agency.math.before.lines", { returnObjects: true }) as { label: string; value: string }[]).map((row, i) => (
                <li key={i} className="flex items-center justify-between border-b border-rule/50 pb-2 last:border-b-0 last:pb-0">
                  <span className="text-sm text-foreground">{row.label}</span>
                  <span className="text-sm font-medium tabular-nums">{row.value}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="border-2 border-foreground bg-background p-6">
            <h3 className="dateline">{t("marketing.pages.agency.math.after.heading")}</h3>
            <ul className="mt-4 space-y-3">
              {(t("marketing.pages.agency.math.after.lines", { returnObjects: true }) as { label: string; value: string }[]).map((row, i) => (
                <li key={i} className="flex items-center justify-between border-b border-rule/50 pb-2 last:border-b-0 last:pb-0">
                  <span className="text-sm text-foreground">{row.label}</span>
                  <span className="text-sm font-medium tabular-nums">{row.value}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>

        <p className="mt-6 text-xs text-ink/60">{t("marketing.pages.agency.math.disclaimer")}</p>
      </Section>

      <Rule />

      {/* White-label highlight with screenshot placeholder */}
      <Section padding="md">
        <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-[1fr_1.2fr]">
          <div>
            <Eyebrow>{t("marketing.pages.agency.whiteLabel.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">
              {t("marketing.pages.agency.whiteLabel.title")}
            </DisplayHeading>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {t("marketing.pages.agency.whiteLabel.lead")}
            </p>
            <ul className="mt-6 space-y-2">
              {(t("marketing.pages.agency.whiteLabel.bullets", { returnObjects: true }) as string[]).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Screenshot placeholder — stylized SVG mock until a real
              white-label console capture is available. */}
          <div className="aspect-[16/10] w-full border border-rule bg-paper">
            <svg viewBox="0 0 640 400" className="h-full w-full text-ink" aria-hidden>
              <rect x="0" y="0" width="640" height="400" fill="currentColor" opacity="0.04" />
              <rect x="0" y="0" width="640" height="40" fill="currentColor" opacity="0.08" />
              <circle cx="20" cy="20" r="5" fill="currentColor" opacity="0.35" />
              <circle cx="38" cy="20" r="5" fill="currentColor" opacity="0.35" />
              <circle cx="56" cy="20" r="5" fill="currentColor" opacity="0.35" />
              <rect x="120" y="14" width="280" height="12" fill="currentColor" opacity="0.25" />
              <rect x="40" y="80"  width="160" height="14" fill="currentColor" opacity="0.6" />
              <rect x="40" y="100" width="100" height="10" fill="currentColor" opacity="0.3" />
              {Array.from({ length: 6 }).map((_, i) => (
                <g key={i} transform={`translate(40, ${140 + i * 38})`}>
                  <rect x="0" y="0" width="560" height="30" fill="currentColor" opacity="0.06" />
                  <rect x="12" y="9" width="120" height="12" fill="currentColor" opacity="0.55" />
                  <rect x="160" y="9" width="80" height="12" fill="currentColor" opacity="0.32" />
                  <rect x="500" y="9" width="50" height="12" fill="currentColor" opacity="0.42" />
                </g>
              ))}
            </svg>
          </div>
        </div>
      </Section>

      <Rule />

      {/* Pricing teaser — Network highlighted */}
      <Section padding="md">
        <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-[1.2fr_1fr]">
          <div>
            <Eyebrow>{t("marketing.pages.agency.pricingTeaser.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">
              {t("marketing.pages.agency.pricingTeaser.title")}
            </DisplayHeading>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {t("marketing.pages.agency.pricingTeaser.lead")}
            </p>
            <Link
              to={withUtm("/tarifs", { source: "segment", campaign: UTM_CAMPAIGN, content: "pricing-teaser" })}
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline"
            >
              {t("marketing.pages.agency.pricingTeaser.cta")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>

          <article className="border border-accent-press bg-background p-6 ring-2 ring-accent-press/30">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-medium tracking-tight">
                  {locale !== "fr" ? PLAN_DISPLAY_NAMES.network.en : PLAN_DISPLAY_NAMES.network.fr}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("marketing.pages.agency.pricingTeaser.audience")}
                </p>
              </div>
              <Badge>{t("marketing.pages.agency.pricingTeaser.recommended")}</Badge>
            </div>
            <div className="mt-6 text-4xl font-medium tabular-nums tracking-tight">{networkPrice}</div>
            <p className="mt-1 text-xs text-muted-foreground">{t("marketing.pages.tarifs.plans.perMonth")}</p>
            <ul className="mt-6 space-y-2">
              {(t("marketing.pages.tarifs.plans.features.network", { returnObjects: true }) as string[])
                .slice(0, 4)
                .map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden />
                    <span>{f}</span>
                  </li>
                ))}
            </ul>
            <Button asChild className="mt-6 w-full">
              <Link to={withUtm("/dpadmin?intent=signup&plan=network", { source: "segment", campaign: UTM_CAMPAIGN, content: "network-card" })}>
                <Sparkles className="mr-2 h-4 w-4" aria-hidden />
                {t("marketing.pages.tarifs.plans.ctaSelect", { plan: locale !== "fr" ? PLAN_DISPLAY_NAMES.network.en : PLAN_DISPLAY_NAMES.network.fr })}
              </Link>
            </Button>
          </article>
        </div>
      </Section>
    </MarketingSegmentLayout>
  );
}
